// The settings model (decision 0046: graphics, audio per bus, controls, accessibility; "saved locally, synced when
// signed in"). The game passes its definitions (ranges, toggles, choices, with defaults); the model keeps values,
// checks every write against its definition (a range clamps and snaps to its step, a choice must be one of its
// options), and tells listeners what changed. Persisting is the game's: it subscribes and writes `values()` to its
// save store. Pure: no DOM.

/** A labelled value of a choice. `label` is a string id. */
export interface SettingOption {
  value: string;
  label: string;
}

/** One setting's definition. `label` is a string id; `group` sorts it into a section. */
export type SettingDef =
  | { id: string; group: string; label: string; kind: "range"; default: number; min: number; max: number; step?: number }
  | { id: string; group: string; label: string; kind: "toggle"; default: boolean }
  | { id: string; group: string; label: string; kind: "choice"; default: string; options: readonly SettingOption[] };

/** A setting's value. */
export type SettingValue = number | boolean | string;

/** The model. */
export interface Settings {
  readonly defs: readonly SettingDef[];
  get<T extends SettingValue = SettingValue>(id: string): T;
  /** Set one value; returns the value kept (clamped or snapped), or throws for an unknown id or a wrong type. */
  set(id: string, value: SettingValue): SettingValue;
  /** Every value, by id (what the game saves). */
  values(): Record<string, SettingValue>;
  /** Load saved values: unknown ids and bad values are skipped and reported; missing ones keep their defaults. */
  load(saved: Record<string, unknown>): string[];
  /** Back to the defaults (one setting, or all). */
  reset(id?: string): void;
  /** Listen to changes (`ids` changed); returns the unsubscribe. */
  subscribe(fn: (ids: string[]) => void): () => void;
}

/** Check a value against its definition: the value to keep, or an error message. */
export function checkSetting(def: SettingDef, v: unknown): { value: SettingValue } | { error: string } {
  switch (def.kind) {
    case "range": {
      if (typeof v !== "number" || !Number.isFinite(v)) return { error: `${def.id}: ${String(v)} is not a number` };
      const c = Math.min(def.max, Math.max(def.min, v));
      const s = def.step ? def.min + Math.round((c - def.min) / def.step) * def.step : c;
      return { value: Number(Math.min(def.max, s).toFixed(6)) };
    }
    case "toggle":
      return typeof v === "boolean" ? { value: v } : { error: `${def.id}: ${String(v)} is not true or false` };
    case "choice":
      return typeof v === "string" && def.options.some((o) => o.value === v) ? { value: v } : { error: `${def.id}: ${String(v)} is not one of its options` };
  }
}

/** Build the model over the game's definitions. */
export function createSettings(defs: readonly SettingDef[]): Settings {
  const byId = new Map(defs.map((d) => [d.id, d]));
  if (byId.size !== defs.length) throw new Error("settings: duplicate ids");
  const values = new Map<string, SettingValue>(defs.map((d) => [d.id, d.default]));
  let listeners: ((ids: string[]) => void)[] = [];
  const emit = (ids: string[]): void => {
    if (ids.length) for (const l of [...listeners]) l(ids);
  };
  const def = (id: string): SettingDef => {
    const d = byId.get(id);
    if (!d) throw new Error(`settings: unknown setting "${id}"`);
    return d;
  };
  return {
    defs,
    get: <T extends SettingValue>(id: string) => {
      def(id);
      return values.get(id) as T;
    },
    set(id, v) {
      const r = checkSetting(def(id), v);
      if ("error" in r) throw new Error(`settings: ${r.error}`);
      if (values.get(id) !== r.value) {
        values.set(id, r.value);
        emit([id]);
      }
      return r.value;
    },
    values: () => Object.fromEntries(values),
    load(saved) {
      const issues: string[] = [];
      const changed: string[] = [];
      for (const [id, v] of Object.entries(saved ?? {})) {
        const d = byId.get(id);
        if (!d) {
          issues.push(`${id}: unknown setting`);
          continue;
        }
        const r = checkSetting(d, v);
        if ("error" in r) issues.push(r.error);
        else if (values.get(id) !== r.value) {
          values.set(id, r.value);
          changed.push(id);
        }
      }
      emit(changed);
      return issues;
    },
    reset(id) {
      const changed: string[] = [];
      for (const d of id ? [def(id)] : defs) {
        if (values.get(d.id) !== d.default) {
          values.set(d.id, d.default);
          changed.push(d.id);
        }
      }
      emit(changed);
    },
    subscribe(fn) {
      listeners.push(fn);
      return () => void (listeners = listeners.filter((l) => l !== fn));
    },
  };
}
